import { ERROR_TEXT } from '@/theme/themes/colors'
import controllerDataConfigApi from '@/api/config/controllerDataConfig'
import { useGuidedEdit } from '@/features/config/contexts/guided-edit-context'
import SearchInput from '@/components/common/SearchInput'
import StatusInterface from '@/components/common/StatusInterface'
import ButtonField from '@/components/form-control/ButtonField'
import { RefreshOneIcon } from '@/components/icons'
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck'
import FullscreenIcon from '@mui/icons-material/Fullscreen'
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit'
import EditIcon from '@mui/icons-material/Edit'
import CancelIcon from '@mui/icons-material/Cancel'
import SaveIcon from '@mui/icons-material/Save'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded'
import { flyInVariantsDashboard, queryKeys } from '@/constant/common'
import useSearchInterfaceId from '@/features/admin/hooks/interface/useSearchInterfaceId'
import { useAuthContext } from '@/features/auth/context'
import { NotifyService } from '@/helpers'
import { usePermission } from '@/hooks/permissions'
import { useTranslation } from '@/hooks/useTranslation'
import useMk2ImportSettling from '@/features/config/hooks/controller/useMk2ImportSettling'
import { TabContext } from '@mui/lab'
import {
  Box,
  CircularProgress,
  SelectChangeEvent,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useQueryClient } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { debounce } from 'lodash'
import { useCallback, useMemo, useRef, useState } from 'react'
import { ConfigLayout } from '../../components/common'
import ConfigHeader from '../../components/common/Header'
import ControllersTableView, {
  AutosaveState,
  ControllersTableViewRef,
} from './ControllersTableView'
import BulkApplyConfigModal from './BulkApplyConfigModal'
import { flushBeforeApply } from './flushBeforeApply'
import classes from './style.module.scss'

// SCRUM-1032: the guided setup's search field, sized and finished like the
// buttons beside it — the shared default is a 32px box with a heavy drop
// shadow, which reads as a leftover next to the wizard's chrome.
const guidedSearchSx = {
  width: 260,
  // The field shares one row with the action buttons, and it is the only thing
  // in that row that can give. Without a floor it kept giving: at an 820px
  // window (a 1440 screen at 175% zoom) it measured 0px wide — an invisible
  // control. 160px still fits a decoder address, and the row as a whole fits
  // the 768px the app supports at all.
  minWidth: 160,
  height: 38,
  px: '12px',
  borderRadius: '10px',
  boxShadow: 'none',
  backgroundColor: 'background.default',
  borderColor: 'border.main',
  transition: 'border-color 160ms ease, box-shadow 160ms ease',
  '&:hover': { borderColor: 'primary.main' },
  '&:focus-within': {
    borderColor: 'primary.main',
    boxShadow: '0 0 0 3px rgba(0,71,68,0.12)',
  },
  '& .MuiInputAdornment-root': { color: 'grey.600', mr: '8px' },
  '& .MuiInputBase-input': {
    fontSize: '13px',
    fontWeight: 400,
    color: 'text.main',
  },
  '& .MuiInputBase-input::placeholder': {
    color: 'grey.600',
    opacity: 1,
  },
}

// Show or hide the rows this user has edited. An "only these" state existed
// too, but a list of nothing but your own edits answers a question nobody was
// asking mid-setup — what is left to do is the one that matters.
type EditedFilter = 'all' | 'notEdited' | 'missingPurpose'

// The clause names what the list is doing, not what the filter is called.
const EDITED_FILTER_LABEL_KEY: Record<EditedFilter, string> = {
  all: 'controllersEditedFilterLabelAll',
  notEdited: 'controllersEditedFilterLabelHiding',
  // Owned by the missing-purpose chip; the edited chip reads as "all" then.
  missingPurpose: 'controllersEditedFilterLabelAll',
}

// The filter runs over the rows loaded so far — the server has no field for
// "this user edited it" — which is worth saying where it applies rather than as
// a caption sitting in the toolbar the whole time.
const EDITED_FILTER_HINT_KEY: Record<EditedFilter, string> = {
  all: 'controllersEditedFilterHintAll',
  notEdited: 'controllersEditedFilterHintHiding',
  missingPurpose: 'controllersEditedFilterHintAll',
}

// The accent the edited rows are tinted with (grid-theme's --grid-touched-*).
const EDITED_ACCENT = { light: '#004744', dark: '#66F0A0' }
const EDITED_ACTIVE_BG = {
  light: 'rgba(0,71,68,0.08)',
  dark: 'rgba(102,240,160,0.12)',
}
// The required-cell red (grid-theme's --grid-invalid-*), light and dark.
const MISSING_ACCENT = ERROR_TEXT
const MISSING_ACTIVE_BG = {
  light: 'rgba(220,53,69,0.08)',
  dark: 'rgba(253,162,155,0.12)',
}
const EDITED_HOVER_BG = {
  light: 'rgba(0,71,68,0.06)',
  dark: 'rgba(102,240,160,0.08)',
}

const AUTOSAVE_TONE: Record<AutosaveState, string> = {
  idle: '#7C7B7B',
  pending: '#C77700',
  saving: '#004744',
  saved: '#2E9E5B',
  error: '#dc3545',
}

const AUTOSAVE_LABEL_KEY: Record<AutosaveState, string> = {
  idle: '',
  // Never surfaced: a "you have unsaved work" nag while the user is mid-edit
  // is noise, since autosave is about to deal with it.
  pending: '',
  saving: 'controllersAutosaveSaving',
  saved: 'controllersAutosaveSaved',
  error: 'controllersAutosaveFailed',
}

interface ControllersProps {
  /**
   * When embedded (e.g. SCRUM-1032 guided setup) the decoder list is scoped to
   * this interface id. Omitted on the normal /config Decoders screen, where the
   * globally-selected interface from AuthContext is used instead.
   */
  interfaceId?: number | string
  /**
   * Render without page-level chrome (ConfigLayout sidebar offset, ConfigHeader,
   * StatusInterface) so the decoder list can sit inside another screen such as
   * the SCRUM-1032 guided setup step.
   */
  embedded?: boolean
  /**
   * Pins the list to one decoder tab and drops the tab bar. The guided setup
   * (SCRUM-1032) gives Output / Input / SDI-12 a step each, so the tabs would
   * be a second, easily-missed navigation for the same thing.
   */
  fixedTab?: string
  /**
   * SCRUM-1032: the host has hidden its chrome and the list owns the viewport.
   * Only affects the toggle's own icon/label — the grid already fills its box.
   */
  focusMode?: boolean
  /**
   * Supplied by the guided setup. When present the toolbar renders the
   * expand/collapse control for it.
   */
  onToggleFocus?: () => void
}

const Controllers = ({
  interfaceId: propInterfaceId,
  embedded = false,
  fixedTab,
  focusMode = false,
  onToggleFocus,
}: ControllersProps = {}) => {
  const MotionDiv = motion.div
  const queryClient = useQueryClient()
  const { interfaceId: authInterfaceId, statusInterfaceId } = useAuthContext()
  const interfaceId = propInterfaceId ?? authInterfaceId
  const { data: statusInterface } = useSearchInterfaceId(interfaceId + '')
  const { isViewOnly } = usePermission()
  const { t } = useTranslation()
  const isDarkTheme = useTheme().palette.mode === 'dark'

  // Refs
  const controllersTableViewRef = useRef<ControllersTableViewRef>(null)

  // State
  const [showLoading, setShowLoading] = useState(false)
  const [selectedTab, setTab] = useState<string>('0')
  const tab = fixedTab ?? selectedTab
  // SCRUM-1032: inside the guided setup the grid opens in bulk edit and its
  // pending rows are flushed when the wizard moves on.
  const bulkEditByDefault = useGuidedEdit('controllers', () =>
    handleSaveBulkEdit(),
  )
  const [bulkEditMode, setBulkEditMode] = useState(bulkEditByDefault)
  const [keyword, setKeyword] = useState('')
  // Cycles All → Not edited → Edited. Three chips would cost toolbar width the
  // decoder columns need more.
  const [editedFilter, setEditedFilter] = useState<EditedFilter>('all')
  const editedChipActive = editedFilter === 'notEdited'
  const missingChipActive = editedFilter === 'missingPurpose'
  const [edits, setEdits] = useState<{
    edited: number
    total: number | null
    missingPurpose: number
  }>({ edited: 0, total: null, missingPurpose: 0 })
  const [isSavingBulkEdit, setIsSavingBulkEdit] = useState(false)
  // SCRUM-1032 round 7 review — a resync rebuilds the controller's IO and
  // deletes what the controller does not report. It must not start while an
  // MK2 import is settling or its automatic resync is running (a second
  // SyncDataIO on the same controller), nor while decoder edits are being
  // written (the resync's re-read lands mid-save).
  const isImportSettling = useMk2ImportSettling(Number(interfaceId || 0))
  // SCRUM-1032: bulk apply one configuration to every output decoder, guided
  // setup only.
  const [applyConfigOpen, setApplyConfigOpen] = useState(false)
  // Writing the staged edits before the run opens (QC round 2, item 3).
  const [preparingApply, setPreparingApply] = useState(false)
  // Autosave status reported by the grid (guided setup only).
  const [autosaveState, setAutosaveState] = useState<AutosaveState>('idle')
  const discoverBlocked =
    isImportSettling || isSavingBulkEdit || autosaveState === 'saving'
  const [lastSavedAt, setLastSavedAt] = useState<number>()

  const [saveProgress, setSaveProgress] = useState<{
    done: number
    total: number
  }>()
  const stopSaveRef = useRef<(() => void) | undefined>()

  const handleAutosaveStateChange = useCallback(
    (
      state: AutosaveState,
      detail?: {
        lastSavedAt?: number
        progress?: { done: number; total: number }
        onStop?: () => void
      },
    ) => {
      setAutosaveState(state)
      setLastSavedAt(detail?.lastSavedAt)
      setSaveProgress(detail?.progress)
      stopSaveRef.current = detail?.onStop
    },
    [],
  )

  // Between saves the pill keeps reporting when the last one landed, so the
  // reassurance stays on screen while the user is still typing.
  const savedAtLabel = lastSavedAt
    ? new Date(lastSavedAt).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      })
    : ''
  const autosaveTone =
    autosaveState === 'saving' || autosaveState === 'error'
      ? AUTOSAVE_TONE[autosaveState]
      : autosaveState === 'saved'
        ? AUTOSAVE_TONE.saved
        : AUTOSAVE_TONE.idle
  const autosaveLabel =
    autosaveState === 'saving'
      ? saveProgress && saveProgress.total > 1
        ? t('controllersAutosaveSavingProgress', {
            done: saveProgress.done,
            total: saveProgress.total,
          })
        : t(AUTOSAVE_LABEL_KEY.saving)
      : autosaveState === 'error'
        ? t(AUTOSAVE_LABEL_KEY.error)
        : autosaveState === 'saved'
          ? t(AUTOSAVE_LABEL_KEY.saved)
          : savedAtLabel
            ? t('controllersAutosaveLastSaved', { time: savedAtLabel })
            : ''

  const handleChangeTab = (_: any, newTab: string) => {
    setBulkEditMode(false)
    setTab(newTab)
  }

  const handleDiscoverControllers = async () => {
    if (isViewOnly || discoverBlocked) return
    setShowLoading(true)
    await controllerDataConfigApi
      .getAutoSyncDecoder(Number(interfaceId || 0))
      .then(() => {
        NotifyService.success(t('controllersResyncSuccess'))
      })
      .catch((e) => NotifyService.error(e))
    queryClient.invalidateQueries([queryKeys.controller])
    /**
     * SCRUM-1032 QC, 5 Oct, item 2 — "resync without new address, sdi-12 is not
     * discovered, unless click resync on sdi-12 page. Resync should always
     * resync new IO" (Wei Woon).
     *
     * `queryKeys.controller` backs the paged grid on /config, and the grid's
     * cache bridge turns that invalidation into a refetch of the tab that is
     * mounted — the one the user is standing on. Guided setup reads each
     * decoder type as its own whole list, `[guidedAllDecoders, interfaceId,
     * type]` with `staleTime: Infinity` (`useSearchAllDecoders`), so the other
     * two types kept the list they loaded until a resync happened while that
     * tab was open.
     *
     * Invalidated without the interface or the type, which is a prefix of all
     * three keys and so reaches every tab. Measured on D2 on 6 Oct before this
     * line existed: a resync refetched `type=1` only, with no request for
     * `type=2` or `type=4`.
     */
    queryClient.invalidateQueries([queryKeys.guidedAllDecoders])
    setShowLoading(false)
  }

  const handleToggleBulkEdit = () => {
    if (isViewOnly) return
    setBulkEditMode(!bulkEditMode)
  }

  const handleSaveBulkEdit = async () => {
    // Answered, not swallowed: `runGuidedSaves` holds the wizard on this step
    // when `failed` is above zero, and this is the function registered as the
    // decoder step's save (SCRUM-1032 QC round 6, item 4).
    if (!controllersTableViewRef.current) return { failed: 0, total: 0 }
    setIsSavingBulkEdit(true)
    try {
      return await controllersTableViewRef.current.handleSave()
    } finally {
      setIsSavingBulkEdit(false)
    }
  }

  // The run reads the decoders when it opens and writes each one whole, so an
  // edit still staged here would be written back to its old value. Flush first,
  // and keep autosave held until the run is closed (`autosavePaused` below).
  const handleOpenApplyConfig = async () => {
    const table = controllersTableViewRef.current
    // The run reads every decoder when it opens; while the import is still
    // landing (or its resync re-reading) it would read a list that is about
    // to change (SCRUM-1032 round 7 follow-up).
    if (isViewOnly || preparingApply || isImportSettling || !table) return
    setPreparingApply(true)
    try {
      const ready = await flushBeforeApply({
        save: () => table.handleSave(),
        hasPendingChanges: table.hasPendingChanges,
      })
      if (ready) setApplyConfigOpen(true)
      else NotifyService.error(t('controllersApplyConfigSaveEditsFirst'))
    } finally {
      setPreparingApply(false)
    }
  }

  const handleCloseApplyConfig = () => {
    setApplyConfigOpen(false)
    // The run changed configurations the list is still showing.
    controllersTableViewRef.current?.refresh()
  }

  const handleCancelBulkEdit = () => {
    controllersTableViewRef.current?.handleCancel()
    setBulkEditMode(false)
  }

  // Search runs on the server (Keyword param), so it matches every decoder
  // rather than only the blocks the grid has already fetched.
  const handleSearch = useMemo(
    () =>
      debounce((event: SelectChangeEvent) => {
        setKeyword(event.target.value.toString().trim())
      }, 500),
    [],
  )

  const handleEditedProgress = useCallback(
    (progress: { edited: number; total: number | null; missingPurpose: number }) =>
      setEdits(progress),
    [],
  )

  const body = (
    <Stack
      className={`${classes.wrapper} ${
        statusInterface &&
        statusInterface.state === 'Offline' &&
        statusInterfaceId &&
        'wrapper-disabled'
      }`}
      sx={embedded ? { height: '100%', minHeight: 0, overflow: 'hidden' } : undefined}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={embedded ? '8px' : '12px'}
        // The row scrolls sideways when it cannot fit rather than clipping its
        // right end: with the step rail taking 240px, the toolbar has no room
        // for every control on a narrow window (or at a high browser zoom),
        // and the last button used to sit off the edge with no way to reach
        // it. It does not wrap — the animation wrapper around each half sets
        // height: 100%, so a second line is drawn over by the grid below.
        sx={{
          gap: 2,
          minWidth: 0,
          width: '100%',
          flexShrink: 0,
          overflowX: 'auto',
          overflowY: 'hidden',
          scrollbarWidth: 'thin',
        }}
      >
        <MotionDiv
          initial="initialRight"
          animate="animate2"
          variants={flyInVariantsDashboard}
          className={classes.header}
          style={{ flex: '1 0 auto', minWidth: 0 }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            {!fixedTab && (
              <TabContext value={tab}>
                <Tabs
                  value={tab}
                  onChange={handleChangeTab}
                  textColor="inherit"
                  sx={{
                    minHeight: '32px',
                    height: '32px',
                    background: '#f1f1f1',
                    width: '360px',
                    flexShrink: 0,
                    borderRadius: '6px',
                    justifyContent: 'center',
                    alignItems: 'center',
                    p: '0px 6px',
                    '.MuiTab-root': {
                      p: '0px',
                      flex: 1,
                      height: '20px',
                      minHeight: '20px',
                      fontWeight: 400,
                      lineHeight: '100%',
                      fontSize: '12px',
                      color: '#5c5c5c',
                      '&.Mui-selected': {
                        color: '#FFFFFF',
                        backgroundColor: '#004744',
                        fontSize: '12px',
                        fontWeight: '400',
                        borderRadius: '6px',
                      },
                    },
                    '.MuiTabs-indicator': {
                      display: 'none',
                    },
                  }}
                >
                  <Tab value={'0'} label={t('controllersTabOutputDecoders')} />
                  <Tab value={'1'} label={t('controllersTabInputDecoders')} />
                  <Tab value={'2'} label={t('controllersTabSdi12Decoders')} />
                </Tabs>
              </TabContext>
            )}
            <SearchInput
              name={t('controllersSearchPlaceholder')}
              onChange={handleSearch}
              sx={embedded ? guidedSearchSx : undefined}
            />
          </Stack>
        </MotionDiv>
        <MotionDiv
          className={classes.header}
          initial="initialLeft"
          animate="animate2"
          variants={flyInVariantsDashboard}
          style={{ flexShrink: 0, maxWidth: '100%' }}
        >
          <Stack direction="row" alignItems="center" sx={{ gap: 1 }}>
            {/* Autosave status. Sits with the toolbar actions rather than over
                the grid: it is a statement about the whole list, and it has to
                be readable at a glance mid-edit. */}
            {embedded &&
              (autosaveState === 'saving' ||
                autosaveState === 'saved' ||
                autosaveState === 'error' ||
                !!lastSavedAt) && (
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.75}
                sx={{
                  height: '32px',
                  px: '12px',
                  borderRadius: '16px',
                  flexShrink: 0,
                  backgroundColor: `${autosaveTone}1F`,
                }}
              >
                {autosaveState === 'saving' ? (
                  <CircularProgress size={13} sx={{ color: autosaveTone }} />
                ) : autosaveState === 'saved' ? (
                  <CheckCircleRoundedIcon
                    sx={{ fontSize: 16, color: autosaveTone }}
                  />
                ) : autosaveState === 'error' ? (
                  <ErrorRoundedIcon sx={{ fontSize: 16, color: autosaveTone }} />
                ) : (
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: autosaveTone,
                    }}
                  />
                )}
                <Typography
                  sx={{
                    fontSize: '13px',
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    color: autosaveTone,
                  }}
                >
                  {autosaveLabel}
                </Typography>

                {/* A long flush has to be interruptible: a first visit to a
                    500-decoder controller writes every blank purpose. */}
                {autosaveState === 'saving' &&
                  !!saveProgress &&
                  saveProgress.total > 20 && (
                    <Typography
                      component="button"
                      onClick={() => stopSaveRef.current?.()}
                      sx={{
                        border: 'none',
                        background: 'none',
                        p: 0,
                        ml: '2px',
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        fontSize: '12px',
                        fontWeight: 600,
                        textDecoration: 'underline',
                        color: autosaveTone,
                      }}
                    >
                      {t('commonLabelsStop')}
                    </Typography>
                  )}
              </Stack>
            )}

            {/* How much of the list this user has edited, and the show / hide
                filter for those rows — one control, since the count is what
                the filter acts on. Its own chip rather than another toolbar
                button: it is a readout first, and it carries the accent the
                edited rows are tinted with. */}
            {/* QC round 2, item 2 — the output decoders still without a
                purpose, and the way to find them in a list of hundreds. Red,
                like the cells it points at; gone once there are none. */}
            {embedded && tab === '0' && (edits.missingPurpose > 0 || missingChipActive) && (
              <Tooltip
                title={t(
                  missingChipActive
                    ? 'controllersMissingPurposeHintShowing'
                    : 'controllersMissingPurposeHint',
                )}
                arrow
                placement="bottom"
              >
                <Stack
                  component="button"
                  type="button"
                  direction="row"
                  alignItems="center"
                  data-testid="toolbar-missing-purpose"
                  onClick={() =>
                    setEditedFilter((current) =>
                      current === 'missingPurpose' ? 'all' : 'missingPurpose',
                    )
                  }
                  sx={{
                    gap: '6px',
                    height: '32px',
                    px: '10px',
                    flexShrink: 0,
                    cursor: 'pointer',
                    borderRadius: '16px',
                    border: '1px solid',
                    fontFamily: 'inherit',
                    transition: 'background-color 140ms ease, border-color 140ms ease',
                    borderColor: (theme) => MISSING_ACCENT[theme.palette.mode],
                    backgroundColor: (theme) =>
                      missingChipActive
                        ? MISSING_ACTIVE_BG[theme.palette.mode]
                        : 'transparent',
                    '&:hover': {
                      backgroundColor: (theme) =>
                        MISSING_ACTIVE_BG[theme.palette.mode],
                    },
                  }}
                >
                  <ErrorRoundedIcon
                    sx={{
                      fontSize: 15,
                      color: (theme) => MISSING_ACCENT[theme.palette.mode],
                    }}
                  />
                  <Typography
                    component="span"
                    sx={{
                      fontSize: '13px',
                      lineHeight: 1,
                      whiteSpace: 'nowrap',
                      fontWeight: 600,
                      color: (theme) => MISSING_ACCENT[theme.palette.mode],
                    }}
                  >
                    {t(
                      missingChipActive
                        ? 'controllersMissingPurposeShowing'
                        : 'controllersMissingPurposeCount',
                      { count: edits.missingPurpose },
                    )}
                  </Typography>
                </Stack>
              </Tooltip>
            )}

            {embedded && (
              <Tooltip
                title={t(EDITED_FILTER_HINT_KEY[editedChipActive ? 'notEdited' : 'all'])}
                arrow
                placement="bottom"
              >
                <Stack
                  component="button"
                  type="button"
                  direction="row"
                  alignItems="center"
                  onClick={() =>
                    setEditedFilter((current) =>
                      current === 'notEdited' ? 'all' : 'notEdited',
                    )
                  }
                  sx={{
                    gap: '8px',
                    height: '32px',
                    px: '10px',
                    flexShrink: 0,
                    cursor: 'pointer',
                    borderRadius: '16px',
                    border: '1px solid',
                    fontFamily: 'inherit',
                    transition: 'background-color 140ms ease, border-color 140ms ease',
                    borderColor: (theme) =>
                      !editedChipActive
                        ? theme.palette.border?.main
                        : EDITED_ACCENT[theme.palette.mode],
                    backgroundColor: (theme) =>
                      !editedChipActive
                        ? 'transparent'
                        : EDITED_ACTIVE_BG[theme.palette.mode],
                    '&:hover': {
                      borderColor: (theme) => EDITED_ACCENT[theme.palette.mode],
                      backgroundColor: (theme) =>
                        !editedChipActive
                          ? EDITED_HOVER_BG[theme.palette.mode]
                          : EDITED_ACTIVE_BG[theme.palette.mode],
                    },
                  }}
                >
                  {/* The same accent the edited rows carry, so the tint in the
                      list has something naming it. */}
                  <Box
                    sx={{
                      width: 3,
                      height: 14,
                      borderRadius: '2px',
                      flexShrink: 0,
                      backgroundColor: (theme) =>
                        EDITED_ACCENT[theme.palette.mode],
                    }}
                  />
                  <Typography
                    component="span"
                    sx={{
                      fontSize: '13px',
                      fontWeight: 600,
                      lineHeight: 1,
                      color: 'text.main',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {edits.edited}
                    {/* A search narrows the list to its matches, so the total
                        would then be describing something else. */}
                    {!keyword && edits.total !== null && (
                      <Box
                        component="span"
                        sx={{ color: 'grey.800', fontWeight: 400 }}
                      >
                        {' / '}
                        {edits.total}
                      </Box>
                    )}
                  </Typography>
                  <Typography
                    component="span"
                    sx={{
                      fontSize: '13px',
                      lineHeight: 1,
                      whiteSpace: 'nowrap',
                      color: (theme) =>
                        !editedChipActive
                          ? theme.palette.grey[800]
                          : EDITED_ACCENT[theme.palette.mode],
                      fontWeight: !editedChipActive ? 400 : 600,
                    }}
                  >
                    {t(EDITED_FILTER_LABEL_KEY[editedChipActive ? 'notEdited' : 'all'])}
                  </Typography>
                </Stack>
              </Tooltip>
            )}

            {/* Expand the list to the whole viewport (guided setup only): a
                controller with hundreds of decoders is a long scroll through a
                12-row window otherwise. */}
            {embedded && onToggleFocus && (
              <ButtonField
                startIcon={
                  focusMode ? <FullscreenExitIcon /> : <FullscreenIcon />
                }
                // Matches the station step's button: same height, radius and
                // outlined treatment, so the one control both steps share does
                // not look like two different controls.
                style={{
                  height: '38px',
                  minWidth: '89px',
                  borderRadius: '8px',
                  fontWeight: 600,
                }}
                sx={{
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'dark' ? '#0d2e3c' : '#ffffff',
                  color: (theme) =>
                    theme.palette.mode === 'dark' ? '#c9d1d9' : '#1f2937',
                  border: (theme) =>
                    `1px solid ${
                      theme.palette.mode === 'dark' ? '#244653' : '#dfe3e6'
                    }`,
                }}
                name={
                  focusMode ? t('controllersExitExpand') : t('commonLabelsExpand')
                }
                onClick={onToggleFocus}
              />
            )}

            {/* Apply a configuration to every output decoder (guided setup
                  only; the Output tab is the only one it applies to). */}
            {embedded && tab === '0' && (
              // Says why it is greyed out while the import settles.
              <Tooltip
                title={isImportSettling ? t('discoveryDataWaitingForImport') : ''}
              >
                <Box component="span" sx={{ display: 'inline-flex' }}>
                  <ButtonField
                    startIcon={
                      preparingApply ? (
                        <CircularProgress sx={{ color: 'white' }} size={14} />
                      ) : (
                        <PlaylistAddCheckIcon />
                      )
                    }
                    style={{
                      height: '32px',
                      minWidth: '110px',
                      borderRadius: '5px',
                      opacity: isViewOnly || isImportSettling ? 0.6 : 1,
                    }}
                    name={
                      preparingApply
                        ? t('commonSaving')
                        : t('controllersApplyConfigButton')
                    }
                    onClick={handleOpenApplyConfig}
                    disabled={isViewOnly || preparingApply || isImportSettling}
                    data-testid="toolbar-apply-config"
                  />
                </Box>
              </Tooltip>
            )}

            {/* SCRUM-1032: the guided wizard's Next flushes every grid, so
                  the per-grid Cancel / Save (and the Edit toggle, since bulk
                  edit is always on there) are hidden inside it. */}
            {!bulkEditByDefault &&
              (!bulkEditMode ? (
                <ButtonField
                  startIcon={<EditIcon />}
                  style={{
                    height: '32px',
                    minWidth: '89px',
                    borderRadius: '5px',
                    opacity: isViewOnly ? 0.6 : 1,
                  }}
                  name={t('commonActionsBulkEdit')}
                  onClick={handleToggleBulkEdit}
                  disabled={isViewOnly}
                />
              ) : (
                <>
                  <ButtonField
                    startIcon={<CancelIcon />}
                    style={{
                      height: '32px',
                      minWidth: '80px',
                      borderRadius: '5px',
                      // A pale pink pill reads as a hole punched in the dark
                      // theme; tint the dark ground instead.
                      backgroundColor: isDarkTheme
                        ? 'rgba(253,162,155,0.12)'
                        : '#fff2f2',
                      color: isDarkTheme ? '#fda29b' : '#dc3545',
                      border: `1px solid ${
                        isDarkTheme ? 'rgba(253,162,155,0.4)' : '#f3c2c5'
                      }`,
                    }}
                    name={t('commonActionsCancel')}
                    onClick={handleCancelBulkEdit}
                  />
                  <ButtonField
                    startIcon={
                      isSavingBulkEdit ? (
                        <CircularProgress sx={{ color: 'white' }} size={14} />
                      ) : (
                        <SaveIcon />
                      )
                    }
                    style={{
                      height: '32px',
                      minWidth: '80px',
                      borderRadius: '5px',
                      backgroundColor: '#004744',
                      color: '#fff',
                    }}
                    name={
                      isSavingBulkEdit
                        ? t('commonSaving')
                        : t('commonActionsSave')
                    }
                    onClick={handleSaveBulkEdit}
                    disabled={isSavingBulkEdit}
                  />
                </>
              ))}

            {/* Discover Button */}
            {showLoading ? (
              <ButtonField
                startIcon={
                  <CircularProgress sx={{ color: 'black' }} size={14} />
                }
                style={{
                  height: '32px',
                  minWidth: '89px',
                  borderRadius: '5px',
                }}
                name={t('controllersSyncing')}
              />
            ) : (
              <ButtonField
                startIcon={<RefreshOneIcon />}
                style={{
                  height: '32px',
                  minWidth: '89px',
                  borderRadius: '5px',
                  opacity: isViewOnly ? 0.6 : 1,
                }}
                name={t('controllersDiscoverButton')}
                onClick={handleDiscoverControllers}
                disabled={isViewOnly || discoverBlocked}
              />
            )}
          </Stack>
        </MotionDiv>
      </Stack>

      <MotionDiv
        animate="animate"
        initial="initial"
        variants={flyInVariantsDashboard}
        className={classes.content}
        style={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}
      >
        <ControllersTableView
          ref={controllersTableViewRef}
          tab={tab}
          interfaceId={Number(interfaceId || 0)}
          keyword={keyword}
          bulkEditMode={bulkEditMode}
          onToggleBulkEdit={handleToggleBulkEdit}
          embedded={embedded}
          onAutosaveStateChange={handleAutosaveStateChange}
          editedFilter={editedFilter}
          onEditedProgress={handleEditedProgress}
          autosavePaused={preparingApply || applyConfigOpen}
        />
      </MotionDiv>
    </Stack>
  )

  // Keyed by tab: the table keeps edits, selection and scroll in its own
  // state, and only a remount clears them when the user switches decoder type.
  return embedded ? (
    <Stack
      key={tab}
      sx={{
        height: '100%',
        width: '100%',
        bgcolor: 'background.default',
      }}
    >
      {body}
      <BulkApplyConfigModal
        open={applyConfigOpen}
        onClose={handleCloseApplyConfig}
        interfaceId={Number(interfaceId || 0)}
      />
    </Stack>
  ) : (
    <ConfigLayout
      key={tab}
      title={t('controllersTitle')}
      header={
        <ConfigHeader subtitle={t('dataConfig')} title={t('controllersTitle')} />
      }
    >
      {body}
      <StatusInterface />
    </ConfigLayout>
  )
}

export default Controllers
